/**
 * The subset of RFC-6902 the `patch` op uses: `add`, `replace`, `remove` over
 * RFC-6901 pointers.
 *
 * A patch exists so a changed field travels as a field — «closed» on one stop of
 * a day plan is six bytes, not the whole route. The applier copies only the
 * nodes on the path and keeps everything else by reference.
 *
 * That sharing does NOT survive the board's validation step: zod rebuilds the
 * object graph on every `safeParse`, and it has to, because the schema fills in
 * defaults. So the sharing here buys a cheap patch and identical rendered
 * output for the untouched rows (React reconciles those to no DOM change), not
 * a skipped re-render. Worth knowing before optimising for the latter.
 *
 * A patch that does not apply is not an error to shout about: the caller keeps
 * the last good props and marks the element stale. Silently applying HALF a
 * patch would be worse than either — hence the copy-then-swap.
 */

/**
 * RFC-6902's own vocabulary. It lived in `transport/events` while the board
 * had an op channel; under A2UI the wire carries no patches at all and this is
 * used for one thing — `updateDataModel`, which is a set-or-delete at a JSON
 * Pointer and is expressed with it.
 */
export type JsonPatchOp = {
  op: 'add' | 'replace' | 'remove';
  path: string;
  value?: unknown;
};

export class PatchError extends Error {}

/** `/days/0/items/2/status` → `['days','0','items','2','status']`. */
function parsePointer(pointer: string): string[] {
  if (pointer === '') return [];
  if (!pointer.startsWith('/')) throw new PatchError(`pointer must start with "/": ${pointer}`);
  return pointer
    .slice(1)
    .split('/')
    .map((token) => token.replace(/~1/g, '/').replace(/~0/g, '~'));
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

function shallowCopy<T>(value: T): T {
  if (Array.isArray(value)) return value.slice() as unknown as T;
  if (isRecord(value)) return { ...value } as unknown as T;
  return value;
}

function applyOne(root: unknown, op: JsonPatchOp): unknown {
  const path = parsePointer(op.path);
  if (path.length === 0) {
    if (op.op === 'remove') throw new PatchError('cannot remove the document root');
    return op.value;
  }

  const copy = shallowCopy(root);
  let parent: unknown = copy;
  for (let i = 0; i < path.length - 1; i += 1) {
    const key = path[i]!;
    if (Array.isArray(parent)) {
      const index = Number.parseInt(key, 10);
      if (!Number.isInteger(index) || index < 0 || index >= parent.length) {
        throw new PatchError(`index ${key} is outside the array at ${op.path}`);
      }
      const next = shallowCopy(parent[index]);
      parent[index] = next;
      parent = next;
    } else if (isRecord(parent)) {
      if (!(key in parent)) throw new PatchError(`no key ${key} on the way to ${op.path}`);
      const next = shallowCopy(parent[key]);
      parent[key] = next;
      parent = next;
    } else {
      throw new PatchError(`${op.path} runs through a scalar`);
    }
  }

  const leaf = path[path.length - 1]!;
  if (Array.isArray(parent)) {
    if (leaf === '-') {
      if (op.op !== 'add') throw new PatchError('"-" is only meaningful for add');
      parent.push(op.value);
      return copy;
    }
    const index = Number.parseInt(leaf, 10);
    if (!Number.isInteger(index) || index < 0) throw new PatchError(`bad array index ${leaf}`);
    if (op.op === 'remove') {
      if (index >= parent.length) throw new PatchError(`cannot remove ${op.path}`);
      parent.splice(index, 1);
    } else if (op.op === 'add') {
      if (index > parent.length) throw new PatchError(`cannot insert at ${op.path}`);
      parent.splice(index, 0, op.value);
    } else {
      if (index >= parent.length) throw new PatchError(`cannot replace ${op.path}`);
      parent[index] = op.value;
    }
    return copy;
  }

  if (!isRecord(parent)) throw new PatchError(`${op.path} does not name a container`);
  if (op.op === 'remove') {
    if (!(leaf in parent)) throw new PatchError(`cannot remove ${op.path}`);
    delete parent[leaf];
  } else if (op.op === 'replace') {
    if (!(leaf in parent)) throw new PatchError(`cannot replace missing ${op.path}`);
    parent[leaf] = op.value;
  } else {
    parent[leaf] = op.value;
  }
  return copy;
}

/**
 * Applies the whole list or none of it. Throws `PatchError` on the first op
 * that does not fit, leaving the caller's original untouched.
 */
export function applyPatch(document: unknown, ops: readonly JsonPatchOp[]): unknown {
  let current = document;
  for (const op of ops) {
    if (op.op !== 'add' && op.op !== 'replace' && op.op !== 'remove') {
      throw new PatchError(`unsupported op ${String((op as { op: string }).op)}`);
    }
    current = applyOne(current, op);
  }
  return current;
}
